import { useEffect, useState, useCallback } from "react";
import { crmApi } from "@/lib/crm";
import { Card, Btn, Input, SectionTitle, Empty, Table, Pill } from "./kit";
import { ShieldCheck, ShieldAlert, KeyRound } from "lucide-react";

export function TeamTab() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales" });
  const [note, setNote] = useState("");
  const load = useCallback(() => crmApi("/staff").then(setStaff).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    setNote("");
    try {
      await crmApi("/staff", { method: "POST", body: form });
      setNote(`${form.name} added as ${form.role}`);
      setForm({ name: "", email: "", password: "", role: "sales" });
      load();
    } catch (err) { setNote(err.response?.data?.detail || "Failed to add staff"); }
  };
  const patch = async (id, body) => { await crmApi(`/staff/${id}`, { method: "PATCH", body }); load(); };
  return (
    <div className="space-y-6" data-testid="crm-team">
      <Card>
        <SectionTitle>Add staff member</SectionTitle>
        <form onSubmit={add} className="grid sm:grid-cols-5 gap-2.5">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" required data-testid="staff-name" />
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" required data-testid="staff-email" />
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (8+ chars)" required data-testid="staff-password" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role"
            className="bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-sm outline-none">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <Btn type="submit" data-testid="staff-add">Add staff</Btn>
        </form>
        {note && <p className="text-sm text-[#f97316] mt-3" data-testid="staff-note">{note}</p>}
      </Card>
      <Card>
        <SectionTitle>Team</SectionTitle>
        <div className="mb-4 flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
          <span className="text-sm font-semibold flex-1">Owner</span>
          <Pill tone="orange">owner</Pill>
          <span className="text-xs text-slate-500">master key — full access</span>
        </div>
        {staff.length === 0 ? <Empty>No named staff yet — add your team above</Empty> : (
          <Table cols={["Name", "Email", "Role", "Status", ""]}>
            {staff.map((s) => (
              <tr key={s.id}>
                <td className="py-2.5 pr-4 font-semibold">{s.name}</td>
                <td className="py-2.5 pr-4">{s.email}</td>
                <td className="py-2.5 pr-4">
                  <select value={s.role} onChange={(e) => patch(s.id, { role: e.target.value })} data-testid="staff-role-edit"
                    className="bg-white/5 border border-white/15 rounded-lg px-2 py-1.5 text-xs outline-none">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="py-2.5 pr-4">{s.active ? <Pill tone="green">active</Pill> : <Pill tone="red">disabled</Pill>}</td>
                <td className="py-2.5">
                  <Btn onClick={() => patch(s.id, { active: !s.active })} data-testid="staff-toggle">
                    {s.active ? "Disable" : "Enable"}
                  </Btn>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <p className="text-xs text-slate-600 mt-4">Roles are enforced on the server: sales, support, marketing and fulfillment each see only their areas. Every unlock and staff change is audit-logged.</p>
      </Card>
    </div>
  );
}

const ROLES = ["owner", "sales", "support", "marketing", "fulfillment"];

export function VisitorsTab() {
  const [visitors, setVisitors] = useState([]);
  useEffect(() => { crmApi("/visitors").then(setVisitors).catch(() => {}); }, []);
  const audience = (v) => {
    if (!v.consent) return { label: "not consented", tone: "slate" };
    if ((v.intent_score || 0) >= 6) return { label: "hot", tone: "red" };
    if ((v.intent_score || 0) >= 3) return { label: "warm", tone: "amber" };
    return { label: "cold", tone: "slate" };
  };
  return (
    <Card data-testid="crm-visitors">
      <SectionTitle>Visitor intelligence</SectionTitle>
      <p className="text-xs text-slate-500 mb-5">First-party, pseudonymous, consent-gated signals only. Audiences become ad-platform eligible only after you connect your own business ad accounts.</p>
      {visitors.length === 0 ? <Empty>No consented visitors tracked yet</Empty> : (
        <Table cols={["Visitor", "Consent", "Intent score", "Events", "Audience", "Last path", "Last seen"]}>
          {visitors.map((v) => {
            const a = audience(v);
            return (
              <tr key={v.visitor_id}>
                <td className="py-2.5 pr-4 font-tech text-xs text-slate-400">{v.visitor_id.slice(0, 8)}…</td>
                <td className="py-2.5 pr-4">{v.consent ? <Pill tone="green">yes</Pill> : <Pill tone="red">no</Pill>}</td>
                <td className="py-2.5 pr-4">{v.intent_score || 0}</td>
                <td className="py-2.5 pr-4">{v.events || 0}</td>
                <td className="py-2.5 pr-4"><Pill tone={a.tone}>{a.label}</Pill></td>
                <td className="py-2.5 pr-4 text-xs text-slate-500">{v.last_path || "/"}</td>
                <td className="py-2.5 text-xs text-slate-500">{(v.last_seen || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            );
          })}
        </Table>
      )}
    </Card>
  );
}

function TotpCard({ onChange }) {
  const [state, setState] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const load = () => crmApi("/security/totp").then((d) => { setState(d); onChange?.(); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startSetup = async () => {
    setNote("");
    try {
      setSetup(await crmApi("/security/totp/setup", { method: "POST" }));
    } catch (e) { setNote(e.response?.data?.detail || "Setup failed"); }
  };
  const enable = async () => {
    setNote("");
    try {
      await crmApi("/security/totp/enable", { method: "POST", body: { code } });
      setSetup(null); setCode("");
      load();
    } catch (e) { setNote(e.response?.data?.detail || "Enable failed"); }
  };
  const disable = async () => {
    setNote("");
    try {
      await crmApi("/security/totp/disable", { method: "POST", body: { code } });
      setCode("");
      load();
    } catch (e) { setNote(e.response?.data?.detail || "Disable failed"); }
  };

  if (!state) return null;
  return (
    <Card data-testid="totp-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <KeyRound size={18} className="text-[#f97316]" />
          <SectionTitle><span className="mb-0">Authenticator (TOTP) protection</span></SectionTitle>
        </div>
        <Pill tone={state.enabled ? "green" : "slate"}>{state.enabled ? "on" : "off"}</Pill>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        {state.enabled
          ? "CRM unlock requires your staff key plus a rotating authenticator code."
          : "Off for now — turn it on any time. You'll need an authenticator app (Google Authenticator, 1Password, Authy…)."}
      </p>
      {!state.enabled && !setup && state.source !== "env" && (
        <Btn onClick={startSetup} data-testid="totp-setup">Set up authenticator</Btn>
      )}
      {setup && (
        <div className="space-y-4 bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid="totp-setup-panel">
          <div>
            <div className="font-tech text-[9px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">1 · Add this secret to your authenticator app</div>
            <code className="block font-tech text-sm text-[#f97316] bg-black/40 rounded-lg px-4 py-3 break-all select-all" data-testid="totp-secret">{setup.secret}</code>
          </div>
          <div>
            <div className="font-tech text-[9px] tracking-[0.2em] uppercase text-slate-500 mb-1.5">2 · Enter the 6-digit code to confirm</div>
            <div className="flex gap-2.5">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} data-testid="totp-code-input" />
              <Btn onClick={enable} data-testid="totp-enable">Turn on</Btn>
            </div>
          </div>
        </div>
      )}
      {state.enabled && state.source !== "env" && (
        <div className="flex gap-2.5 max-w-sm">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Current code to turn off" maxLength={6} data-testid="totp-disable-input" />
          <Btn onClick={disable} data-testid="totp-disable">Turn off</Btn>
        </div>
      )}
      {note && <p className="text-xs text-red-400 mt-3" data-testid="totp-note">{note}</p>}
    </Card>
  );
}

export function SecurityTab() {
  const [data, setData] = useState(null);
  const load = () => crmApi("/security/overview").then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Empty>Loading security center…</Empty>;
  return (
    <div className="space-y-6" data-testid="crm-security">
      <Card className="flex items-center gap-8">
        <div className="text-center">
          <div className="font-display font-extrabold text-5xl text-[#f97316]" data-testid="security-score">{data.score}%</div>
          <div className="font-tech text-[9px] tracking-[0.2em] uppercase text-slate-500 mt-1">Config health</div>
        </div>
        <p className="text-xs text-slate-500 max-w-md">{data.note} Complete the unchecked items (WAF, backups, TOTP, strict CORS) as production hardening before launch.</p>
      </Card>
      <TotpCard onChange={load} />
      <Card>
        <SectionTitle>Controls</SectionTitle>        <div className="grid sm:grid-cols-2 gap-2.5">
          {data.checks.map((c) => (
            <div key={c.key} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg px-3.5 py-2.5">
              {c.ok ? <ShieldCheck size={16} className="text-emerald-400 shrink-0" /> : <ShieldAlert size={16} className="text-amber-400 shrink-0" />}
              <span className="text-sm text-slate-300">{c.label}</span>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <SectionTitle>Audit events</SectionTitle>
        {data.audit_events.length === 0 ? <Empty>No security events logged</Empty> : (
          <ul className="space-y-2">
            {data.audit_events.map((e) => (
              <li key={e.id} className="flex items-center gap-3 text-xs bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5">
                <Pill tone="red">{e.type}</Pill>
                <span className="font-tech text-slate-500">{e.source_hash}</span>
                <span className="text-slate-600 ml-auto">{(e.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
